import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/store/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import toast from 'react-hot-toast';

// Validation schema
const registerSchema = z.object({
    username: z
        .string()
        .min(1, 'El nombre de usuario es requerido')
        .min(3, 'El nombre de usuario debe tener al menos 3 caracteres')
        .max(50, 'El nombre de usuario no puede tener más de 50 caracteres')
        .regex(/^[a-zA-Z0-9_]+$/, 'Solo se permiten letras, números y guiones bajos'),
    email: z
        .string()
        .min(1, 'El email es requerido')
        .email('Ingresa un email válido'),
    password: z
        .string()
        .min(1, 'La contraseña es requerida')
        .min(6, 'La contraseña debe tener al menos 6 caracteres')
        .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'La contraseña debe contener al menos una mayúscula, una minúscula y un número'),
    confirmPassword: z
        .string()
        .min(1, 'Confirma tu contraseña'),
    firstName: z
        .string()
        .min(1, 'El nombre es requerido')
        .min(2, 'El nombre debe tener al menos 2 caracteres')
        .max(50, 'El nombre no puede tener más de 50 caracteres'),
    lastName: z
        .string()
        .min(1, 'El apellido es requerido')
        .min(2, 'El apellido debe tener al menos 2 caracteres')
        .max(50, 'El apellido no puede tener más de 50 caracteres'),
    phone: z
        .string()
        .optional()
        .refine((val) => !val || val.length >= 9, 'El teléfono debe tener al menos 9 dígitos'),
    acceptTerms: z
        .boolean()
        .refine((val) => val === true, 'Debes aceptar los términos y condiciones'),
    acceptMarketing: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get('redirect') || '/';

    const { register: registerUser, isLoading } = useAuthStore();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [step, setStep] = useState(1);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setError,
        watch,
        trigger,
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema),
        mode: 'onChange',
        defaultValues: {
            username: '',
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            phone: '',
            acceptTerms: false,
            acceptMarketing: false,
        },
    });

    const password = watch('password');

    const onSubmit = async (data: RegisterFormData) => {
        try {
            await registerUser({
                username: data.username,
                email: data.email,
                password: data.password,
                first_name: data.firstName,
                last_name: data.lastName,
                phone: data.phone || undefined,
            });

            toast.success('¡Cuenta creada exitosamente! Bienvenido a Eisgemi');
            navigate(redirectTo, { replace: true });
        } catch (error: any) {
            const message = error?.response?.data?.message || 'Error al crear la cuenta';

            // Handle specific validation errors
            if (error?.response?.data?.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    const mappedField = field === 'first_name' ? 'firstName' :
                        field === 'last_name' ? 'lastName' : field;
                    setError(mappedField as keyof RegisterFormData, {
                        message: serverErrors[field][0],
                    });
                });
            } else {
                toast.error(message);
            }
        }
    };

    const handleNextStep = async () => {
        const fieldsToValidate = step === 1
            ? ['username', 'email', 'password', 'confirmPassword']
            : ['firstName', 'lastName'];

        const isValid = await trigger(fieldsToValidate as any);
        if (isValid) {
            setStep(step + 1);
        }
    };

    const passwordRequirements = [
        { met: password?.length >= 6, text: 'Al menos 6 caracteres' },
        { met: /[a-z]/.test(password || ''), text: 'Una letra minúscula' },
        { met: /[A-Z]/.test(password || ''), text: 'Una letra mayúscula' },
        { met: /\d/.test(password || ''), text: 'Un número' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl shadow-xl p-8"
                >
                    {/* Header */}
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center space-x-2 mb-6">
                            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold text-xl">T</span>
                            </div>
                            <span className="text-2xl font-bold text-gray-900">Eisgemi</span>
                        </Link>

                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            Crear cuenta
                        </h2>
                        <p className="text-gray-600">
                            Únete a miles de usuarios que ya disfrutan de nuestras ofertas
                        </p>
                    </div>

                    {/* Progress Indicator */}
                    <div className="flex items-center justify-center mb-8">
                        <div className="flex items-center space-x-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                                }`}>
                                1
                            </div>
                            <div className={`w-16 h-1 rounded ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'
                                }`} />
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                                }`}>
                                2
                            </div>
                            <div className={`w-16 h-1 rounded ${step >= 3 ? 'bg-primary-600' : 'bg-gray-200'
                                }`} />
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 3 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                                }`}>
                                3
                            </div>
                        </div>
                    </div>

                    {/* Registration Form */}
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        {/* Step 1: Account Details */}
                        {step === 1 && (
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                className="space-y-4"
                            >
                                <div className="text-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900">Datos de la cuenta</h3>
                                    <p className="text-sm text-gray-600">Configura tu usuario y contraseña</p>
                                </div>

                                <Input
                                    {...register('username')}
                                    type="text"
                                    label="Nombre de usuario"
                                    placeholder="usuario123"
                                    leftIcon={<User className="w-5 h-5" />}
                                    error={errors.username?.message}
                                    fullWidth
                                />

                                <Input
                                    {...register('email')}
                                    type="email"
                                    label="Email"
                                    placeholder="tu@email.com"
                                    leftIcon={<Mail className="w-5 h-5" />}
                                    error={errors.email?.message}
                                    fullWidth
                                />

                                <div className="relative">
                                    <Input
                                        {...register('password')}
                                        type={showPassword ? 'text' : 'password'}
                                        label="Contraseña"
                                        placeholder="Tu contraseña"
                                        leftIcon={<Lock className="w-5 h-5" />}
                                        error={errors.password?.message}
                                        fullWidth
                                    />

                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>

                                {/* Password Requirements */}
                                {password && (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                                            Requisitos de contraseña:
                                        </h4>
                                        <div className="space-y-1">
                                            {passwordRequirements.map((req, index) => (
                                                <div key={index} className="flex items-center space-x-2">
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.met ? 'bg-green-500' : 'bg-gray-300'
                                                        }`}>
                                                        {req.met && <Check className="w-3 h-3 text-white" />}
                                                    </div>
                                                    <span className={`text-sm ${req.met ? 'text-green-600' : 'text-gray-600'
                                                        }`}>
                                                        {req.text}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="relative">
                                    <Input
                                        {...register('confirmPassword')}
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        label="Confirmar contraseña"
                                        placeholder="Repite tu contraseña"
                                        leftIcon={<Lock className="w-5 h-5" />}
                                        error={errors.confirmPassword?.message}
                                        fullWidth
                                    />

                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>

                                <Button
                                    type="button"
                                    onClick={handleNextStep}
                                    fullWidth
                                    size="lg"
                                    rightIcon={<ArrowRight className="w-5 h-5" />}
                                >
                                    Continuar
                                </Button>
                            </motion.div>
                        )}

                        {/* Step 2: Personal Info */}
                        {step === 2 && (
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                className="space-y-4"
                            >
                                <div className="text-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900">Información personal</h3>
                                    <p className="text-sm text-gray-600">Completa tus datos personales</p>
                                </div>

                                <Input
                                    {...register('firstName')}
                                    type="text"
                                    label="Nombre"
                                    placeholder="Tu nombre"
                                    leftIcon={<User className="w-5 h-5" />}
                                    error={errors.firstName?.message}
                                    fullWidth
                                />

                                <Input
                                    {...register('lastName')}
                                    type="text"
                                    label="Apellido"
                                    placeholder="Tu apellido"
                                    leftIcon={<User className="w-5 h-5" />}
                                    error={errors.lastName?.message}
                                    fullWidth
                                />

                                <Input
                                    {...register('phone')}
                                    type="tel"
                                    label="Teléfono (opcional)"
                                    placeholder="+51 987 654 321"
                                    leftIcon={<Phone className="w-5 h-5" />}
                                    error={errors.phone?.message}
                                    fullWidth
                                />

                                <div className="flex space-x-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setStep(1)}
                                        className="flex-1"
                                    >
                                        Atrás
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={handleNextStep}
                                        className="flex-1"
                                        rightIcon={<ArrowRight className="w-5 h-5" />}
                                    >
                                        Continuar
                                    </Button>
                                </div>
                            </motion.div>
                        )}

                        {/* Step 3: Terms and Confirmation */}
                        {step === 3 && (
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                className="space-y-6"
                            >
                                <div className="text-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900">Términos y condiciones</h3>
                                    <p className="text-sm text-gray-600">Lee y acepta nuestros términos</p>
                                </div>

                                {/* Terms and Conditions */}
                                <div className="space-y-4">
                                    <label className="flex items-start space-x-3">
                                        <input
                                            {...register('acceptTerms')}
                                            type="checkbox"
                                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">
                                            Acepto los{' '}
                                            <Link to="/terms" className="text-primary-600 hover:text-primary-500 underline">
                                                términos y condiciones
                                            </Link>{' '}
                                            y la{' '}
                                            <Link to="/privacy" className="text-primary-600 hover:text-primary-500 underline">
                                                política de privacidad
                                            </Link>
                                        </span>
                                    </label>
                                    {errors.acceptTerms && (
                                        <p className="text-sm text-red-600">{errors.acceptTerms.message}</p>
                                    )}

                                    <label className="flex items-start space-x-3">
                                        <input
                                            {...register('acceptMarketing')}
                                            type="checkbox"
                                            className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">
                                            Acepto recibir promociones y ofertas especiales por email (opcional)
                                        </span>
                                    </label>
                                </div>

                                {/* Summary */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Resumen de tu cuenta:</h4>
                                    <div className="space-y-1 text-sm text-gray-600">
                                        <p><span className="font-medium">Usuario:</span> {watch('username')}</p>
                                        <p><span className="font-medium">Email:</span> {watch('email')}</p>
                                        <p><span className="font-medium">Nombre:</span> {watch('firstName')} {watch('lastName')}</p>
                                        {watch('phone') && (
                                            <p><span className="font-medium">Teléfono:</span> {watch('phone')}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex space-x-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setStep(2)}
                                        className="flex-1"
                                    >
                                        Atrás
                                    </Button>
                                    <Button
                                        type="submit"
                                        loading={isLoading || isSubmitting}
                                        className="flex-1"
                                        rightIcon={<Check className="w-5 h-5" />}
                                    >
                                        Crear cuenta
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </form>

                    {/* Footer */}
                    <div className="text-center mt-6">
                        <p className="text-sm text-gray-600">
                            ¿Ya tienes una cuenta?{' '}
                            <Link
                                to={`/login${redirectTo !== '/' ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`}
                                className="text-primary-600 hover:text-primary-500 font-medium"
                            >
                                Inicia sesión
                            </Link>
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default RegisterPage;